import { Grid, Skeleton } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { AppBox, AppButton, AppTypography } from 'components'
import { AdminSettingsHelpModules, ApiQueryKeys } from 'globals/constants'
import { centsToDollar, getMDYFormattedDate, timestampToDate } from 'globals/functions'
import { useSnackbar } from 'notistack'
import { FC, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { AdminUserApi, CancelMembershipPayload, MembershipApi, ShowAlert } from 'sdk'
import { useAppDispatch } from 'store/app.store'
import { AuthSliceSelector, setOpenedAdminSettingsHelpModule } from 'store/slices'

interface Props {
  userId: string
  closeDialog: () => void
  isSubAccount?: boolean
  refetch?: () => void
}

export const ManageAccountSubscriptionPlanCard: FC<Props> = ({ userId, closeDialog, isSubAccount, refetch }) => {
  const adminUserApi = new AdminUserApi()
  const membershipApi = new MembershipApi()
  const { enqueueSnackbar } = useSnackbar()
  const dispatch = useAppDispatch()
  const { OwnerId } = useSelector(AuthSliceSelector)

  const {
    mutate,
    data,
    isPending: isGetting,
  } = useMutation({
    mutationFn: (userId: string) => adminUserApi.getUserSubscriptionInfo(userId),
    mutationKey: [ApiQueryKeys.admin.getUserSubscriptionInfo],
    onError: (error) => enqueueSnackbar(error?.message, { variant: 'error' }),
  })
  const { mutate: cancelMembership, isPending: isCanceling } = useMutation({
    mutationKey: [ApiQueryKeys.memberships.CancelMembership],
    mutationFn: (payload: CancelMembershipPayload) => membershipApi.CancelMembership(payload),
    onError: (err) => enqueueSnackbar(err.message, { variant: 'error' }),
    onSuccess: () => {
      if (refetch) {
        ShowAlert(
          'info',
          `Please manually disable this user's account if you wish to limit their access to ParkSitesIO.`
        )
        refetch()
      }
    },
  })
  const handleInvoiceClick = () => {
    dispatch(
      setOpenedAdminSettingsHelpModule({
        key: AdminSettingsHelpModules.Invoices,
        options: { Id: userId!, Title: 'Invoices' },
      })
    )
    closeDialog()
  }
  const discountPercent = useMemo(() => {
    if (!data?.SubAccountsCount || data?.SubAccountsCount < 4) {
      return 0
    } else if (data?.SubAccountsCount > 4) {
      return data.SubAccountsCount > 9 ? 10 : 5
    }
    return 0
  }, [data])

  const totalAmount = useMemo(() => {
    const totalSeats = (data?.SubAccountsCount ?? 0) + 1
    const basePrice = data?.CurrentPlan?.MembershipPrice
    const basePriceDollar = basePrice && basePrice > 0 ? +(basePrice / 100).toFixed(2) : 0
    const totalAmount = totalSeats * basePriceDollar
    const discountedPrice = discountPercent > 0 ? +((totalAmount * discountPercent) / 100).toFixed(2) : 0
    return totalAmount - discountedPrice
  }, [data, discountPercent])

  useEffect(() => {
    mutate(isSubAccount && OwnerId ? OwnerId : userId)
  }, [])

  const handlForceCancelPlanClick = () => {
    closeDialog()
    ShowAlert('question', `Are you sure you want to cancel this user's plan?`).then((x) => {
      if (x.isConfirmed) {
        cancelMembership({
          UserId: userId,
          Reason: 'Admin_Disabled',
          IsAdminForced: true,
        })
      }
    })
  }
  return (
    <AppBox backgroundColor="grey.100" border={1} borderColor={'grey.200'} borderRadius={6} padding={2} width={'100%'}>
      <AppTypography fontWeight={700} fontSize={16}>
        Subscription Plan
      </AppTypography>
      {isGetting ? (
        <Grid container spacing={6}>
          <Grid item xs={6}>
            <AppBox display={'flex'} flexDirection={'column'} gap={1}>
              <Skeleton height={30} width={150} />
              <Skeleton height={30} width={150} />
              <Skeleton height={30} width={150} />
              <Skeleton height={30} width={150} />
            </AppBox>
          </Grid>
          <Grid item xs={6}>
            <AppBox display={'flex'} flexDirection={'column'} gap={1}>
              <Skeleton height={30} width={150} />
              <Skeleton height={30} width={150} />
              <Skeleton height={30} width={150} />
              <Skeleton height={30} width={150} />
            </AppBox>
          </Grid>
        </Grid>
      ) : (
        <>
          <AppBox paddingY={2} If={!!data?.CurrentPlan || !!data?.IsTrial}>
            <AppBox display={'flex'} gap={1}>
              <AppTypography color={'grey.700'}>Plan Owner:</AppTypography>
              <AppTypography fontWeight={700}>{data?.PlanOwner}</AppTypography>
            </AppBox>
            {data?.IsCancelled && !data?.CurrentPlan ? (
              <AppBox display={'flex'} gap={1}>
                <AppTypography color={'grey.700'}>Plan Cancelled:</AppTypography>
                <AppTypography fontWeight={700}></AppTypography>
              </AppBox>
            ) : data?.IsTrial ? (
              <>
                <AppBox display={'flex'} gap={1}>
                  <AppTypography color={'grey.700'}>Current Plan:</AppTypography>
                  <AppTypography fontWeight={700}>Trial Period</AppTypography>
                </AppBox>
                <AppBox display={'flex'} gap={1}>
                  <AppTypography color={'grey.700'}>Trial Expiring:</AppTypography>
                  <AppTypography fontWeight={700}>
                    {getMDYFormattedDate(new Date(data.TrialExpiry ?? ''))}
                  </AppTypography>
                </AppBox>
              </>
            ) : (
              <>
                <AppBox display={'flex'} gap={1} paddingBottom={1}>
                  {!!data?.CurrentPlan && (
                    <>
                      <AppTypography color={'grey.700'}>Current Plan:</AppTypography>
                      <AppTypography fontWeight={700}>
                        {data.CurrentPlan?.MembershipTitle} - {centsToDollar(data.CurrentPlan?.MembershipPrice ?? 0)} (
                        {data.CurrentPlan?.MembershipBilled}){' '}
                      </AppTypography>
                    </>
                  )}
                </AppBox>
                <Grid container>
                  <Grid item xs={6}>
                    <AppBox display={'flex'} gap={1}>
                      {!!data?.CurrentPlan && (
                        <>
                          <AppTypography color={'grey.700'}>Renewal Date:</AppTypography>
                          <AppTypography fontWeight={700}>
                            {getMDYFormattedDate(new Date(data?.CurrentPlan?.CurrentPeriodEnd ?? ''))}
                          </AppTypography>
                        </>
                      )}
                    </AppBox>

                    <AppBox display={'flex'} gap={1}>
                      <AppTypography color={'grey.700'}>Total Seats</AppTypography>
                      <AppTypography fontWeight={700}>{(data?.SubAccountsCount ?? 0) + 1}</AppTypography>
                    </AppBox>
                    <AppBox display={'flex'} gap={1}>
                      <AppTypography color={'grey.700'}>Total Discount:</AppTypography>
                      <AppTypography fontWeight={700}>{discountPercent}%</AppTypography>
                    </AppBox>
                    <AppBox display={'flex'} gap={1}>
                      <AppTypography color={'grey.700'}>Charge Date:</AppTypography>
                      <AppTypography fontWeight={700}>
                        {data?.CurrentPlan && getMDYFormattedDate(new Date(data.CurrentPlan.CurrentPeriodEnd ?? ''))}
                      </AppTypography>
                    </AppBox>
                  </Grid>
                  <Grid item xs={6}>
                    <AppBox display={'flex'} gap={1}>
                      <AppTypography color={'grey.700'}>Next Plan:</AppTypography>
                      <AppTypography fontWeight={700}>
                        {data?.IsPausedSoon
                          ? 'Pause'
                          : data?.IsCancelledSoon
                            ? 'Cancel'
                            : data?.FuturePlan?.MembershipTitle ?? data?.CurrentPlan?.MembershipTitle ?? ''}
                      </AppTypography>
                    </AppBox>
                    <AppBox display={'flex'} gap={1}>
                      {data?.CurrentPlan && (
                        <>
                          <AppTypography color={'grey.700'}>Price Per Seat:</AppTypography>
                          <AppTypography fontWeight={700}>
                            {centsToDollar(data.CurrentPlan?.MembershipPrice ?? 0)}
                          </AppTypography>
                        </>
                      )}
                    </AppBox>
                    <AppBox display={'flex'} gap={1}>
                      <AppTypography color={'grey.700'}>Total Charges:</AppTypography>
                      <AppTypography fontWeight={700}>${totalAmount}</AppTypography>
                    </AppBox>
                    <AppBox display={'flex'} gap={1}>
                      <AppTypography color={'grey.700'}>Payment Status:</AppTypography>
                      <AppTypography fontWeight={700}>{data?.PaymentStatus}</AppTypography>
                    </AppBox>
                  </Grid>
                </Grid>
              </>
            )}
          </AppBox>
          {!isSubAccount && (
            <AppBox
              display={'flex'}
              justifyContent={!data?.IsCancelledSoon && data?.CurrentPlan ? 'space-between' : 'flex-end'}
            >
              {!data?.IsCancelledSoon && data?.CurrentPlan && (
                <AppButton
                  variant="contained"
                  color="error"
                  sidePaddings={2}
                  size="small"
                  onClick={handlForceCancelPlanClick}
                >
                  Force Plan Cancellation
                </AppButton>
              )}
              <AppBox If={!!data?.CurrentPlan || data?.IsTrial}>
                <AppButton
                  variant="contained"
                  color="primary"
                  sidePaddings={2}
                  size="small"
                  onClick={handleInvoiceClick}
                >
                  Invoice
                </AppButton>
              </AppBox>
            </AppBox>
          )}
        </>
      )}
    </AppBox>
  )
}
